import { CommonModule } from '@angular/common';
import { Component, HostBinding, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import {
  TimeSeriesPivotTableDataService,
} from '@backoffice-frontend/controlling/domain';
import { EnumService } from '@backoffice-frontend/shared/bo/util-masterdata';
import { TimeSeries } from '@clean-code/models';
import { ActionButtonModule } from '@clean-code/shared/components/ui-action-button';
import { UiMatTableModule } from '@clean-code/shared/components/ui-mat-table';
import { PageContainerModule } from '@clean-code/shared/components/ui-page-container';
import { UtilDateModule } from '@clean-code/shared/util/util-date';
import { TranslocoModule } from '@jsverse/transloco';
import {
  DxPivotGridModule,
  DxPivotGridTypes,
} from 'devextreme-angular/ui/pivot-grid';
import { CellClickEvent } from 'devextreme/ui/pivot_grid';
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import { PivotGridTotalDisplayMode } from 'devextreme/ui/pivot_grid_types';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  map,
  Observable,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { PivotTableConfigService } from './data/feature-pivot-table-config.service';
import { PivotTableEventsService } from './data/pivot-table-events.service';
import { PivotTableService } from './data/pivot-table.service';

@Component({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatSelectModule,
    DxPivotGridModule,
    UiMatTableModule,
    PageContainerModule,
    TranslocoModule,
    ActionButtonModule,
    MatCardModule,
    UtilDateModule,
    RouterModule,
  ],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'pivot-table',
  templateUrl: './feature-pivot-table.component.html',
  styleUrls: ['./feature-pivot-table.component.scss'],
})
export class PivotTableComponent {
  @HostBinding('class.dataTableList') dataTableList = true;
  private enumService = inject(EnumService);
  public isLoading$ = new BehaviorSubject<boolean>(false);
  private refreshTable$ = this.pivotTableService.refreshTableObservable;
  private rangeState: { start: Date; end: Date } = { start: null, end: null };
  private categoryState: string;
  private marketAreaState: number | null = null;
  private typeState: string | null = null;
  private initStates = false;
  // public pivotGridTotalDisplayMode: PivotGridTotalDisplayMode = '';
  pivotGridTotalDisplayMode: PivotGridTotalDisplayMode = 'none';
  public parserTypes$ = this.enumService.getEnumType$('ParserValueType');

  public identifierCategoriesFormControl = new UntypedFormControl();
  public marketAreaFormControl = new UntypedFormControl({
    value: null,
    disabled: true,
  });
  public typeFormControl = new UntypedFormControl({
    value: null,
    disabled: true,
  });

  public range = new FormGroup({
    start: new FormControl<Date | null>({
      value: this.getOneMonthAgo(),
      disabled: true,
    }),
    end: new FormControl<Date | null>({ value: new Date(), disabled: true }),
  });

  private getOneMonthAgo(): Date {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() - 1);
    return currentDate;
  }

  public marketAreas$ = this.identifierCategoriesFormControl.valueChanges.pipe(
    switchMap((x) => {
      return this.service.marketAreaGetAll(x.id);
    }),
  );

  public timeSeriesIdentifierCategoriesLocalized$ =
    this.service.getAllTimeSeriesIdentifierCategoriesLocalized$();

  constructor(
    protected service: TimeSeriesPivotTableDataService,
    protected pivotTableConfigService: PivotTableConfigService,
    private pivotTableService: PivotTableService,
    private pivotTableEventsService: PivotTableEventsService,
  ) {}

  public dataSource$: Observable<PivotGridDataSource> = combineLatest([
    this.identifierCategoriesFormControl.valueChanges,
    this.range.valueChanges.pipe(startWith(this.range.value)),
    this.marketAreaFormControl.valueChanges.pipe(startWith(null)),
    this.typeFormControl.valueChanges.pipe(startWith(null)),
    this.refreshTable$,
  ]).pipe(
    filter(([category, range, marketArea, type, refreshTable]) => {
      const isCategoryValid = category != null;
      const isRangeValid = range.start != null && range.end != null;
      if (category !== this.categoryState) {
        this.initStates = true;
      }

      const isChanged =
        this.rangeState.end !== range.end ||
        this.categoryState !== category ||
        this.marketAreaState !== marketArea ||
        this.typeState !== type;

      this.typeState = undefined;
      this.marketAreaState = undefined;
      return (isCategoryValid && isRangeValid && isChanged) || refreshTable;
    }),
    tap(([category]) => {
      if (this.categoryState !== category) {
        this.marketAreaFormControl.setValue(null, { emitEvent: false });
        this.typeFormControl.setValue(null, { emitEvent: false });
        this.range.disable({ emitEvent: false });
        this.marketAreaFormControl.disable({ emitEvent: false });
        this.typeFormControl.disable({ emitEvent: false });
      }

      this.isLoading$.next(true);
    }),
    switchMap(([category, range, marketArea, type, _]) => {
      const validMarketArea =
        marketArea != undefined && this.categoryState === category
          ? marketArea
          : null;

      const validType =
        type != undefined && this.categoryState === category ? type : null;

      this.rangeState = range as { start: Date; end: Date };
      this.categoryState = category;
      this.marketAreaState = marketArea;
      this.typeState = type;
      this.initStates = false;
      return this.service
        .getAll$(category.id, range, validMarketArea, validType)
        .pipe(
          switchMap((data: TimeSeries[]) => {
            return combineLatest([
              this.service.getTimeSeriesIdsFromTimeSeriesHistory(),
              this.service.getDuplicateData(),
            ]).pipe(
              tap(([result, duplicatedData]) => {
                this.pivotTableEventsService.setSources(
                  data,
                  result,
                  duplicatedData,
                );
              }),
              map(() => {
                return this.pivotTableConfigService.dataSourceConfig(data);
              }),
            );
          }),
          finalize(() => {
            this.range.enable({ emitEvent: false });
            this.marketAreaFormControl.enable({ emitEvent: false });
            this.typeFormControl.enable({ emitEvent: false });
            this.pivotTableService.setRefreshTable(false);
          }),
        );
    }),
  );

  onCellClick(event: CellClickEvent) {
    return this.pivotTableEventsService.onCellClick(event);
  }

  onAddClick(event: CellClickEvent) {
    return this.pivotTableEventsService.onAddClick(event);
  }

  onCellPrepared(event: DxPivotGridTypes.CellPreparedEvent) {
    return this.pivotTableEventsService.onCellPrepared(event);
  }

  onContentReady(e: any) {
    this.isLoading$.next(false);
  }
}
